import { Button } from "@/components/ui/Button";
import { formatSoldOutNightGerman } from "@/lib/soldOutDates";

type SoldOutNightsEditorProps = {
  soldOutNights: string[];
  soldOutMessage: string;
  newSoldOutDate: string;
  canWrite: boolean;
  dateMin?: string;
  dateMax?: string;
  dateError?: string | null;
  helperText?: string;
  onSoldOutMessageChange: (value: string) => void;
  onNewSoldOutDateChange: (value: string) => void;
  onAddSoldOutDate: () => void;
  onRemoveSoldOutDate: (iso: string) => void;
};

export function SoldOutNightsEditor({
  soldOutNights,
  soldOutMessage,
  newSoldOutDate,
  canWrite,
  dateMin,
  dateMax,
  dateError,
  helperText,
  onSoldOutMessageChange,
  onNewSoldOutDateChange,
  onAddSoldOutDate,
  onRemoveSoldOutDate,
}: SoldOutNightsEditorProps) {
  return (
    <>
      <div className="space-y-2">
        <span className="block text-sm font-medium text-label">Ausgebuchte Nächte</span>
        <p className="text-xs text-label-secondary">
          Ein eingetragenes Datum bezieht sich auf die folgende Nacht. Anreise an diesem Tag ist
          nicht möglich, Abreise am selben Tag schon. Übernachtungen über ausgebuchte Nächte werden
          verhindert.
        </p>
        {helperText ? <p className="text-xs text-label-secondary">{helperText}</p> : null}
        {soldOutNights.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {soldOutNights.map((iso) => (
              <li
                key={iso}
                className="flex items-center justify-between rounded-lg border border-separator px-3 py-2"
              >
                <span>{formatSoldOutNightGerman(iso)}</span>
                {canWrite ? (
                  <Button type="button" variant="secondary" onClick={() => onRemoveSoldOutDate(iso)}>
                    Entfernen
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-label-secondary">Keine ausgebuchten Nächte hinterlegt.</p>
        )}
        {canWrite ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              Datum hinzufügen
              <input
                type="date"
                className="eh-input"
                value={newSoldOutDate}
                min={dateMin}
                max={dateMax}
                onChange={(e) => onNewSoldOutDateChange(e.target.value)}
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              disabled={!newSoldOutDate || Boolean(dateError)}
              onClick={onAddSoldOutDate}
            >
              Hinzufügen
            </Button>
          </div>
        ) : null}
        {dateError ? (
          <p className="text-sm text-red-600" role="alert">
            {dateError}
          </p>
        ) : null}
      </div>

      <label className="block min-w-0 text-sm">
        <span className="font-medium text-label">Hinweistext bei Ausgebucht</span>
        <textarea
          className="eh-input mt-1 min-h-[4rem] max-w-xl"
          value={soldOutMessage}
          onChange={(e) => onSoldOutMessageChange(e.target.value)}
          placeholder="Der Campingplatz ist an diesem Tag leider ausgebucht."
          disabled={!canWrite}
        />
        <span className="mt-0.5 block text-xs text-label-secondary">
          Wird in der Online-Buchung angezeigt, wenn der gewählte Zeitraum ausgebuchte Nächte
          enthält.
        </span>
      </label>
    </>
  );
}
